import type { HeaderProps } from "./types";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Search, Button as DsButton } from "@digdir/designsystemet-react";
import {
  MagnifyingGlassIcon,
  MenuHamburgerIcon,
  XMarkIcon,
} from "@navikt/aksel-icons";

import { MegaMenu } from "../megamenu";
import Button from "@/components/button";

export default function Header(props: HeaderProps) {
  const headerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  const [searchVisible, setSearchVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [menuVisible, setMenuVisible] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(100);

  useLayoutEffect(() => {
    if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
  }, []);

  const toggleVisible = (el: string) => {
    if (el === "search") {
      setMenuVisible(false);
      setSearchVisible(!searchVisible);
    }
    if (el === "menu") {
      setSearchVisible(false);
      setMenuVisible(!menuVisible);
    }
  };

  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setSearchTerm("");
  };

  // Autofocus search input when opened
  useEffect(() => {
    if (searchVisible && inputRef.current) {
      inputRef.current.focus();
    }
  }, [searchVisible]);

  // Close search on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        searchVisible &&
        searchRef.current &&
        !searchRef.current.contains(e.target as Node) &&
        !headerRef.current?.contains(e.target as Node)
      ) {
        setSearchVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [searchVisible]);

  return (
    <div
      className="relative flex z-30 pt-10 pb-5 bg-default border-b border-neutralNsf-subtle md:py-[30px]"
      ref={headerRef}
    >
      <div className="flex w-full max-w-content items-center justify-between mx-auto px-4">
        <a href="/" title="Skiforbundet.no">
          <img
            src="/assets/svg/logo-color.svg"
            alt="Logo, Norges Skiforbund"
            className="h-10 md:h-[50px] xl:h-15"
          />
        </a>
        <div className="flex items-center gap-x-3.5 md:gap-x-4 xl:gap-x-4.5">
          {/* "Bli klubbmedlem"-button */}
          {props.memberPageUrl && (
            <DsButton
              aria-label="Bli medlem i din lokale skiklubb"
              variant="primary"
              className="hidden min-h-12 h-12 border lg:flex xl:h-14"
              asChild
            >
              <a href={props.memberPageUrl} className="text-lg">
                Bli klubbmedlem
              </a>
            </DsButton>
          )}

          {/* Search button */}
          <Button
            onClick={() => toggleVisible("search")}
            aria-label={props.t.submit}
            variant="secondary"
            className={`max-sm:!px-0 ${
              searchVisible ? "bg-main-surface-active" : ""
            }`}
          >
            {searchVisible ? (
              <>
                <span className="hidden sm:flex">{props.t.submit}</span>
                <XMarkIcon aria-hidden="true" />
              </>
            ) : (
              <>
                <span className="hidden sm:flex">{props.t.submit}</span>
                <MagnifyingGlassIcon aria-hidden="true" />
              </>
            )}
          </Button>

          {/* Menu button */}
          <Button
            aria-label="Meny"
            onClick={() => toggleVisible("menu")}
            onMouseDown={(e) => e.stopPropagation()}
            variant="secondary"
            className={`max-sm:!px-0 ${
              menuVisible ? "bg-main-surface-active" : ""
            }`}
          >
            {menuVisible ? (
              <>
                <span className="hidden sm:flex">Meny</span>
                <XMarkIcon aria-hidden="true" />
              </>
            ) : (
              <>
                <span className="hidden sm:flex">Meny</span>
                <MenuHamburgerIcon aria-hidden="true" />
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Search */}
      <div
        ref={searchRef}
        className={`absolute top-full z-20 w-full mt-[1px] px-4 bg-tinted-neutral shadow-lg transition duration-150 ease-out 
          ${
            searchVisible
              ? "opacity-100 translate-y-0 h-52 pt-14 pb-24"
              : "opacity-0 -translate-y-2 pointer-events-none h-0"
          }
        `}
      >
        {searchVisible && (
          <form
            className="mx-auto w-full max-w-2xl"
            action={props.searchPageUrl}
            method="get"
          >
            <Search>
              <Search.Input
                ref={inputRef}
                id="q"
                name="q"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={props.t.placeholder}
                aria-label={props.t.placeholder}
                className="border border-neutralNsf"
              />
              <Search.Clear onClick={handleClear} />
              <Search.Button />
            </Search>
          </form>
        )}
      </div>

      {/* Megamenu */}
      <MegaMenu
        visible={menuVisible}
        megaMenuData={props.megaMenu}
        headerHeight={headerHeight}
        memberPageUrl={props.memberPageUrl}
        onClose={() => setMenuVisible(false)}
      />
    </div>
  );
}